import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Modal";
import { ButtonShadcn } from "@/components/ui/Button";
import RHFInput from "@/components/form/RHFInput";
import RHFTextArea from "@/components/form/RHFTextArea";
import RHFCheckbox from "@/components/form/RHFCheckbox";

import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { candidateStatusFlowValidationSchema } from "../validation.schema";

const AddEditFlowModal = ({
  isOpen,
  mode = "add",
  defaultValues,
  flow,
  onModelClose,
  onHandleSubmit,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const isEdit = mode === "edit";

  const methods = useForm({
    resolver: yupResolver(candidateStatusFlowValidationSchema),
    defaultValues,
    mode: "onChange",
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      setInlineError("");
    }
  }, [defaultValues, isOpen, reset]);

  const submit = async (values) => {
    try {
      setSubmitting(true);
      setInlineError("");
      await onHandleSubmit(values);
    } catch (e) {
      setInlineError(apiErrorMessageFormat(e, `Failed to ${isEdit ? "update" : "create"} flow`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onModelClose?.()}>
      <DialogContent className="w-[480px] md:w-full flex flex-col p-0">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">
              {isEdit ? "Rename Status Flow" : "New Status Flow"}
            </DialogTitle>
            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
              {isEdit
                ? "Update this flow's name or description."
                : "A new flow starts empty — add its steps from the status list once it's created."}
            </DialogDescription>
          </div>
          <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submit)} className="flex flex-col min-h-0 flex-1">
            <div className="space-y-3 p-5 overflow-y-auto min-h-0">
              <RHFInput
                name="name"
                label="Flow Name"
                placeholder="e.g. Senior Developer"
                required
              />
              <RHFTextArea
                name="description"
                label="Description"
                placeholder="What kind of role is this flow for?"
                rows={2}
                counter="never"
                className="[&>div:last-child]:!min-h-0 [&>div:last-child]:mt-0"
                inputClass="max-h-[160px] overflow-y-auto resize-y"
              />

              {isEdit ? (
                flow?.isDefault ? (
                  <p className="text-xs text-text-secondary">
                    This is the tenant's Default flow — it's always active and can't be
                    deactivated or deleted.
                  </p>
                ) : (
                  <div className="-mt-1 rounded-lg border border-border-primary bg-background-secondary px-3 py-2.5">
                    <RHFCheckbox
                      name="isActive"
                      label="Active — selectable when creating or editing a job"
                    />
                    <p className="ml-6 mt-1 text-xs leading-relaxed text-text-secondary">
                      Turning this off only hides the flow from new job selections. Jobs and
                      candidates already on it are untouched.
                    </p>
                  </div>
                )
              ) : null}

              {inlineError ? (
                <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
                  {inlineError}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border-primary bg-background-secondary shrink-0">
              <ButtonShadcn
                type="button"
                variant="tertiary"
                size="lg"
                onClick={onModelClose}
                className="rounded-full px-5"
              >
                Cancel
              </ButtonShadcn>
              <ButtonShadcn
                type="submit"
                variant="secondary"
                size="lg"
                disabled={submitting}
                className="rounded-full px-5"
              >
                {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create Flow"}
              </ButtonShadcn>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditFlowModal;
